import { useId, useRef, useState, type ClipboardEvent, forwardRef, type ReactNode } from 'react';
import { useResizeObserver } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { ErrorMessage, Icon, Text } from '@shared/widgets';
import EyeIcon from '@assets/icons/eye.svg';
import CrossedEyeIcon from '@assets/icons/crossed-eye.svg';

const PREFIX_PADDING = 24;

interface BaseInputProps {
  className?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  label?: ReactNode;
  onlyNumeric?: boolean;
  onlyPositive?: boolean;
  noSpaces?: boolean;
  prefix?: string;
  error?: ReactNode;
  type?: 'number' | 'email' | 'password' | 'text';
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  value?: string | number;
  onChange?: (data: string) => void;
  onBlur?: () => void;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>((props, ref) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const prefixRef = useRef<HTMLDivElement | null>(null);
  const id = useId();

  const { width: prefixWidth = 0 } = useResizeObserver({ ref: prefixRef, box: 'border-box' });
  return (
    <div className={cn(props.className)}>
      {props.label && (
        <Text htmlFor={id} tag='label' size='sm' color='secondary'>
          {props.label} {props.required ? ' *' : ''}
        </Text>
      )}
      <div className={cn('relative flex', props.wrapperClassName)}>
        <div
          ref={prefixRef}
          className={cn('pointer-events-none absolute left-5 top-1/2 z-10 hidden -translate-y-1/2 select-none', props.prefix && 'block')}
        >
          {props.prefix}
        </div>
        <input
          ref={ref}
          id={id}
          className={cn(
            'flex w-full p-2 outline-none transition-all duration-200',
            PaletteUtils.isInsigniaPalette && [
              'h-10 border-b border-b-light bg-ice text-sm font-light focus:font-normal focus:text-soft-black dark:bg-soft-black',
              'hover:bg-white',
              'focus:border-b-primary focus:bg-white dark:focus:text-white',
            ],
            PaletteUtils.isVcfcPalette && [
              'h-[50px] rounded-[100px] border border-light/70 bg-white px-5 dark:border-transparent dark:bg-white/10',
              'hover:border-primary dark:hover:border-light/70',
              'focus:border-primary dark:focus:border-light/70',
            ],
            props.type === 'password' && 'pr-10',
            props.readOnly && 'pointer-events-none opacity-30',
            props.disabled && 'pointer-events-none opacity-30',
            props.inputClassName,
          )}
          style={props.prefix ? { paddingLeft: `${prefixWidth + PREFIX_PADDING}px` } : undefined}
          type={props.type === 'password' && showPassword ? 'text' : (props.type ?? 'text')}
          placeholder={props.required ? `${props.placeholder} *` : props.placeholder}
          required={props.required}
          readOnly={props.readOnly}
          minLength={props.minLength}
          maxLength={props.maxLength}
          disabled={props.disabled}
          value={props.value}
          onChange={(e) => {
            let value = e.target.value ?? '';
            if (props.onlyNumeric) value = value.replace(/\D+/g, '');
            props.onChange?.(value);
          }}
          onBlur={props.onBlur}
          onKeyDown={(e) => {
            if (props.onlyPositive && ['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
            if (props.noSpaces && [' '].includes(e.key)) e.preventDefault();
          }}
          onPaste={props.onPaste}
          onWheel={(e) => props.type === 'number' && e.currentTarget.blur()}
        />
        {props.type === 'password' && (
          <Icon
            className={cn(
              'absolute cursor-pointer',
              PaletteUtils.isVcfcPalette && 'right-3 top-3',
              PaletteUtils.isInsigniaPalette && 'right-2 top-2',
            )}
            icon={
              showPassword ? (
                <EyeIcon className={cn(PaletteUtils.isVcfcPalette && 'text-light-gray')} />
              ) : (
                <CrossedEyeIcon className={cn(PaletteUtils.isVcfcPalette && 'text-light-gray')} />
              )
            }
            onClick={() => setShowPassword((prev) => !prev)}
          />
        )}
      </div>
      <ErrorMessage className='text-left' text={props.error} />
    </div>
  );
});
